import { addDays, type LocalDate } from '../dates';
import { addMonthsToMonth, dayInMonth, monthOf } from './schedule';
import type { InstallmentLike, PayeeLike } from './types';

type CardDueFields = Pick<PayeeLike, 'dueDays' | 'statementDay' | 'dueDaysAfterStatement'>;
type CardDates = CardDueFields & Pick<PayeeLike, 'trackFrom'>;

export const cardDueDay = (card: Pick<PayeeLike, 'dueDays'>): number => card.dueDays[0] ?? 1;

/** Days after the statement the payment is due, or null for a fixed due day of the month. */
export const dueAfterDays = (card: Pick<PayeeLike, 'dueDaysAfterStatement'>): number | null =>
  card.dueDaysAfterStatement != null && card.dueDaysAfterStatement > 0
    ? card.dueDaysAfterStatement
    : null;

export interface CardCycle {
  statement: LocalDate;
  due: LocalDate;
}

/**
 * One billing cycle of a card, keyed by a month ('YYYY-MM'). Fixed due day: the cycle whose due
 * date falls in that month, paying the last statement before it. "N days after the statement":
 * the statement that closes in that month, due N days later (Oct 13 + 25 = Nov 7, Feb 13 + 25 =
 * Mar 10), so every statement has exactly one due even when months differ in length.
 */
export function cardCycle(card: CardDueFields, month: string): CardCycle {
  const after = dueAfterDays(card);
  if (after !== null) {
    const statement = dayInMonth(month, card.statementDay ?? 1);
    return { statement, due: addDays(statement, after) };
  }
  const due = dayInMonth(month, cardDueDay(card));
  return { statement: statementDateFor(card, due), due };
}

/**
 * v0.7.6: the cycle a card purchase on `date` is billed on: the first statement that closes on or
 * after the purchase (a purchase on the statement day is on that statement), and its due date.
 * The same window cardDues uses (after the previous statement, up to this one).
 */
export function purchaseCycle(card: CardDueFields, date: LocalDate): CardCycle {
  // The cycle keyed by the month before can't close on or after a date in this month, so start there.
  let month = addMonthsToMonth(monthOf(date), -1);
  for (let i = 0; i < 4; i += 1, month = addMonthsToMonth(month, 1)) {
    const c = cardCycle(card, month);
    if (c.statement >= date) return c;
  }
  return cardCycle(card, month);
}

/** The card's payment due date of a cycle (see cardCycle). */
export function cardDueDateIn(card: CardDueFields, month: string): LocalDate {
  return cardCycle(card, month).due;
}

/**
 * The statement a due date pays. Fixed due day: the last statement day strictly before it. N days
 * after: the last statement day on or before the due date minus N.
 */
export function statementDateFor(
  card: Pick<PayeeLike, 'statementDay'> & Partial<Pick<PayeeLike, 'dueDaysAfterStatement'>>,
  due: LocalDate,
): LocalDate {
  const day = card.statementDay ?? 1;
  const after = dueAfterDays({ dueDaysAfterStatement: card.dueDaysAfterStatement ?? null });
  if (after !== null) {
    const latest = addDays(due, -after);
    const same = dayInMonth(monthOf(latest), day);
    return same <= latest ? same : dayInMonth(addMonthsToMonth(monthOf(latest), -1), day);
  }
  const same = dayInMonth(monthOf(due), day);
  return same < due ? same : dayInMonth(addMonthsToMonth(monthOf(due), -1), day);
}

/** The statement before this one (one month earlier, clamped to that month's length). */
export function previousStatement(card: Pick<PayeeLike, 'statementDay'>, s: LocalDate): LocalDate {
  return dayInMonth(addMonthsToMonth(monthOf(s), -1), card.statementDay ?? 1);
}

/**
 * The cycle an installment month is charged on: the one due in that month. With "N days after the
 * statement" the cycle is keyed by its statement month, so a due that usually lands in the next
 * month (13th + 25 days) is charged on the previous month's statement.
 */
export function installmentCycleMonth(card: CardDueFields, month: string): string {
  const after = dueAfterDays(card);
  if (after === null) return month;
  const shift = Math.floor(((card.statementDay ?? 1) + after - 1) / 30);
  return addMonthsToMonth(month, -shift);
}

/**
 * Whether a statement is tracked in Tahi (closed on or after the day the card was added). Earlier
 * statements were spent before Tahi: their amount is the card's opening balance, and installment
 * months on them write no expense.
 */
export function statementTracked(card: CardDates, due: LocalDate): boolean {
  return statementDateFor(card, due) >= card.trackFrom;
}

/** 'YYYY-MM' of the last charge. */
export function installmentEndMonth(i: Pick<InstallmentLike, 'startMonth' | 'months'>): string {
  return addMonthsToMonth(i.startMonth, Math.max(1, i.months) - 1);
}

export interface InstallmentCharge {
  installmentId: string;
  /** 1-based month number ("3 of 12"). */
  n: number;
  month: string;
  /** The card's due date that month: the expense's date. */
  date: LocalDate;
  amountMinor: number;
  /** False for months on statements from before the card was added (no expense, counted as paid). */
  tracked: boolean;
}

/** Every month of an installment: one charge per month, never the full price (decision 06). */
export function installmentCharges(i: InstallmentLike, card: CardDates): InstallmentCharge[] {
  return Array.from({ length: Math.max(0, i.months) }, (_, k) => {
    const month = addMonthsToMonth(i.startMonth, k);
    const date = cardDueDateIn(card, installmentCycleMonth(card, month));
    return {
      installmentId: i.id,
      n: k + 1,
      month,
      date,
      amountMinor: i.monthlyMinor,
      tracked: statementTracked(card, date),
    };
  });
}

export interface InstallmentProgress {
  totalMinor: number;
  monthsPaid: number;
  monthsLeft: number;
  remainingMinor: number;
  endMonth: string;
  finished: boolean;
  /** The next charge, or null once finished. */
  next: InstallmentCharge | null;
}

/** Months paid count every month whose charge date has come, before Tahi or not. */
export function installmentProgress(
  i: InstallmentLike,
  card: CardDates,
  today: LocalDate,
): InstallmentProgress {
  const charges = installmentCharges(i, card);
  const paid = charges.filter((c) => c.date <= today).length;
  return {
    totalMinor: i.monthlyMinor * i.months,
    monthsPaid: paid,
    monthsLeft: i.months - paid,
    remainingMinor: i.monthlyMinor * (i.months - paid),
    endMonth: installmentEndMonth(i),
    finished: paid >= i.months,
    next: charges.find((c) => c.date > today) ?? null,
  };
}

/** Months whose charge date has come, on tracked statements: each needs its expense. */
export function dueInstallmentCharges(
  i: InstallmentLike,
  card: CardDates,
  today: LocalDate,
): InstallmentCharge[] {
  return installmentCharges(i, card).filter((c) => c.tracked && c.date <= today);
}
